import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Popover,
  TextField,
  MenuItem,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TripForm from './TripForm';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rateFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

const formatRate = (value) => {
  if (value == null || !Number.isFinite(value)) return 'Not set';
  return `${rateFormatter.format(value)}/mi`;
};

const formatCurrency = (value) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return currencyFormatter.format(value);
};

const computeDeduction = (trip) => {
  if (!trip) return null;
  const { totalMileage, mileageRate } = trip;
  if (totalMileage == null || mileageRate == null) return null;
  if (!Number.isFinite(totalMileage) || !Number.isFinite(mileageRate)) return null;
  return totalMileage * mileageRate;
};

const MileageLog = ({
  user,
  trips,
  odometerYearStart,
  odometerYearEnd,
  defaultMileageRate,
  isLoading,
  error,
  setError,
  selectedYear,
  setSelectedYear,
  fetchMileage,
  createTrip,
  updateTrip,
  deleteTrip,
  updateOdometer,
  updateMileageRate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deletingTripId, setDeletingTripId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Year-settings editing state (odometer + default rate)
  const [editingOdometer, setEditingOdometer] = useState(false);
  const [odometerForm, setOdometerForm] = useState({ start: '', end: '', rate: '' });
  const [rateError, setRateError] = useState('');
  const [isSavingOdometer, setIsSavingOdometer] = useState(false);
  const [rateUpdatedNotice, setRateUpdatedNotice] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    if (dateStr.includes('T')) {
      return new Date(dateStr).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  // Form popover handlers
  const handleOpenForm = (event, trip = null) => {
    setAnchorEl(event.currentTarget);
    setEditingTrip(trip);
  };

  const handleCloseForm = () => {
    setAnchorEl(null);
    setEditingTrip(null);
  };

  const handleSubmitTrip = async (formData, tripId) => {
    setIsSubmitting(true);
    setError('');
    try {
      const success = tripId
        ? await updateTrip(tripId, formData)
        : await createTrip(formData);
      if (success) {
        handleCloseForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (event, tripId) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeletingTripId(tripId);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteAnchorEl(null);
    setDeletingTripId(null);
    setIsDeleting(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!deletingTripId || isDeleting) return;
    setIsDeleting(true);
    const success = await deleteTrip(deletingTripId);
    if (success) {
      handleDeleteConfirmClose();
    } else {
      setIsDeleting(false);
    }
  };

  // Year-settings handlers
  const handleEditOdometer = () => {
    setOdometerForm({
      start: odometerYearStart != null ? String(odometerYearStart) : '',
      end: odometerYearEnd != null ? String(odometerYearEnd) : '',
      rate: defaultMileageRate != null ? String(defaultMileageRate) : '',
    });
    setRateError('');
    setEditingOdometer(true);
  };

  const handleCancelOdometer = () => {
    setEditingOdometer(false);
    setRateError('');
  };

  const handleSaveOdometer = async () => {
    setRateError('');

    const rawRate = odometerForm.rate.trim();
    let parsedRate = null;
    if (rawRate !== '') {
      parsedRate = parseFloat(rawRate);
      if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
        setRateError('Rate must be a positive number');
        return;
      }
    }

    setIsSavingOdometer(true);

    const odometerPayload = {};
    if (odometerForm.start !== '') odometerPayload.odometerYearStart = parseFloat(odometerForm.start);
    if (odometerForm.end !== '') odometerPayload.odometerYearEnd = parseFloat(odometerForm.end);

    const odometerChanged = Object.keys(odometerPayload).length > 0 && (
      odometerPayload.odometerYearStart !== odometerYearStart ||
      odometerPayload.odometerYearEnd !== odometerYearEnd
    );

    const rateValue = rawRate === '' ? null : parsedRate;
    const rateChanged = rateValue !== (defaultMileageRate ?? null);

    let odometerOk = true;
    if (odometerChanged) {
      odometerOk = await updateOdometer(odometerPayload);
    }

    let rateOk = true;
    if (odometerOk && rateChanged) {
      rateOk = await updateMileageRate(rateValue);
      if (rateOk) {
        setRateUpdatedNotice(true);
      }
    }

    if (odometerOk && rateOk) {
      setEditingOdometer(false);
    }
    setIsSavingOdometer(false);
  };

  // Auto-dismiss the rate-updated info banner after 8 seconds
  useEffect(() => {
    if (!rateUpdatedNotice) return undefined;
    const timer = setTimeout(() => setRateUpdatedNotice(false), 8000);
    return () => clearTimeout(timer);
  }, [rateUpdatedNotice]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'flex-start',
        gap: 2,
        mb: 3,
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Mileage Log
        </Typography>
        <TextField
          select
          size="small"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          sx={{ minWidth: 120 }}
          aria-label="Select calendar year"
        >
          {yearOptions.map((year) => (
            <MenuItem key={year} value={year}>{year}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={(e) => handleOpenForm(e)}
          sx={{ textTransform: 'none' }}
        >
          Add Trip
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError('')}
          sx={{ mb: 3 }}
          aria-live="polite"
        >
          {error}
        </Alert>
      )}

      {/* Rate-updated info banner */}
      {rateUpdatedNotice && (
        <Alert
          severity="info"
          onClose={() => setRateUpdatedNotice(false)}
          sx={{ mb: 3 }}
          aria-live="polite"
        >
          Default rate updated. Existing trips were not changed; only new trips will use the new rate.
        </Alert>
      )}

      {/* Year Settings Card */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {selectedYear} Year Settings
          </Typography>
          {!editingOdometer && (
            <Tooltip title="Edit Year Settings">
              <IconButton size="small" onClick={handleEditOdometer} aria-label="Edit year settings">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {editingOdometer ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5 }} alignItems="flex-end">
            <TextField
              label="Year-Start Odometer"
              type="number"
              size="small"
              value={odometerForm.start}
              onChange={(e) => setOdometerForm(prev => ({ ...prev, start: e.target.value }))}
              inputProps={{ min: '0', step: '0.1' }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Year-End Odometer"
              type="number"
              size="small"
              value={odometerForm.end}
              onChange={(e) => setOdometerForm(prev => ({ ...prev, end: e.target.value }))}
              inputProps={{ min: '0', step: '0.1' }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Default Mileage Rate ($/mi)"
              type="number"
              size="small"
              value={odometerForm.rate}
              onChange={(e) => {
                setOdometerForm(prev => ({ ...prev, rate: e.target.value }));
                if (rateError) setRateError('');
              }}
              inputProps={{ min: '0', step: '0.001' }}
              sx={{ minWidth: 180 }}
              error={Boolean(rateError)}
              helperText={rateError || undefined}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveOdometer}
                disabled={isSavingOdometer}
                startIcon={<SaveIcon />}
                sx={{ textTransform: 'none' }}
              >
                {isSavingOdometer ? 'Saving...' : 'Save'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCancelOdometer}
                disabled={isSavingOdometer}
                startIcon={<CancelIcon />}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Year-Start: {odometerYearStart != null ? odometerYearStart.toLocaleString() + ' mi' : 'Not set'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Year-End: {odometerYearEnd != null ? odometerYearEnd.toLocaleString() + ' mi' : 'Not set'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Default Rate: {formatRate(defaultMileageRate)}
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* Trip Table / Loading / Empty State */}
      {isLoading ? (
        <TableContainer component={Paper}>
          <Table aria-label="Mileage log">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {!isMobile && <TableCell>Odometer Start</TableCell>}
                {!isMobile && <TableCell>Odometer End</TableCell>}
                <TableCell align="right">Total Mileage</TableCell>
                {!isMobile && <TableCell align="right">Rate</TableCell>}
                <TableCell align="right">Deduction</TableCell>
                {!isMobile && <TableCell>Notes</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  {!isMobile && <TableCell><Skeleton variant="text" width={70} /></TableCell>}
                  {!isMobile && <TableCell><Skeleton variant="text" width={70} /></TableCell>}
                  <TableCell align="right"><Skeleton variant="text" width={50} /></TableCell>
                  {!isMobile && <TableCell align="right"><Skeleton variant="text" width={70} /></TableCell>}
                  <TableCell align="right"><Skeleton variant="text" width={70} /></TableCell>
                  {!isMobile && <TableCell><Skeleton variant="text" width={120} /></TableCell>}
                  <TableCell align="center"><Skeleton variant="text" width={140} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : trips.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <DirectionsCarIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No trips recorded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Add Trip" to log your first trip for {selectedYear}.
          </Typography>
        </Box>
      ) : (
        <>
          {(() => {
            const { total, excluded } = trips.reduce((acc, trip) => {
              const d = computeDeduction(trip);
              if (d == null) return { total: acc.total, excluded: acc.excluded + 1 };
              return { total: acc.total + d, excluded: acc.excluded };
            }, { total: 0, excluded: 0 });
            return (
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Annual Deduction: {formatCurrency(total)}
                </Typography>
                {excluded > 0 && (
                  <Tooltip title={`Excludes ${excluded} trip${excluded === 1 ? '' : 's'} missing a rate or mileage`}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary', gap: 0.5 }}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Excludes {excluded} trip{excluded === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            );
          })()}
          <TableContainer component={Paper}>
            <Table aria-label="Mileage log">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {!isMobile && <TableCell>Odometer Start</TableCell>}
                  {!isMobile && <TableCell>Odometer End</TableCell>}
                  <TableCell align="right">Total Mileage</TableCell>
                  {!isMobile && <TableCell align="right">Rate</TableCell>}
                  <TableCell align="right">Deduction</TableCell>
                  {!isMobile && <TableCell>Notes</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trips.map((trip) => {
                  const deduction = computeDeduction(trip);
                  return (
                    <TableRow key={trip.tripId} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                      <TableCell>{formatDate(trip.tripDate)}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          {trip.tripOdometerStart != null ? trip.tripOdometerStart.toLocaleString() : '-'}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          {trip.tripOdometerEnd != null ? trip.tripOdometerEnd.toLocaleString() : '-'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {trip.totalMileage != null ? trip.totalMileage.toFixed(1) : '-'}
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="right">{formatRate(trip.mileageRate)}</TableCell>
                      )}
                      <TableCell align="right">{formatCurrency(deduction)}</TableCell>
                      {!isMobile && (
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {trip.notes || '-'}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={(e) => handleOpenForm(e, trip)} aria-label={`Edit trip on ${trip.tripDate}`}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, trip.tripId)} aria-label={`Delete trip on ${trip.tripDate}`}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Trip Form Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseForm}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <TripForm
          trip={editingTrip}
          onSubmit={handleSubmitTrip}
          onCancel={handleCloseForm}
          isSubmitting={isSubmitting}
          selectedYear={selectedYear}
          defaultMileageRate={defaultMileageRate}
        />
      </Popover>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteConfirmClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deleting a trip is irreversible. Are you sure you want to delete this trip?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleDeleteConfirmClose}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Go Back
            </Button>
            <Button
              onClick={handleDeleteConfirmed}
              variant="contained"
              color="error"
              disabled={isDeleting}
              sx={{ textTransform: 'none' }}
            >
              {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes, Delete'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default MileageLog;
